import { ApiError } from "@/lib/apiClient";
import {
  MarketingTemplate,
  CreateMarketingTemplateDto,
  UpdateMarketingTemplateDto,
  FetchMarketingTemplatesOptions,
  MarketingTemplateFilters,
} from "@/types/models/marketing";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface MarketingTemplatesState {
  items: MarketingTemplate[];
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  currentTemplate: MarketingTemplate | null;
  templateLoading: boolean;
  templateError: ApiError | null;

  searchResults: MarketingTemplate[];
  searchLoading: boolean;
  searchError: ApiError | null;
  searchPagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  formState: {
    loading: boolean;
    error: ApiError | null;
    success: boolean;
  };

  filters: MarketingTemplateFilters;
  fetchOptions: FetchMarketingTemplatesOptions;
}

const initialState: MarketingTemplatesState = {
  items: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  currentTemplate: null,
  templateLoading: false,
  templateError: null,

  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchPagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  formState: {
    loading: false,
    error: null,
    success: false,
  },

  filters: {},
  fetchOptions: {
    page: 1,
    page_size: 10,
    sort_by: "created_at",
    sort_order: "desc",
  },
};

const marketingTemplateSlice = createSlice({
  name: "marketingTemplates",
  initialState,
  reducers: {
    fetchMarketingTemplatesRequest: (
      state,
      action: PayloadAction<FetchMarketingTemplatesOptions>,
    ) => {
      state.loading = true;
      state.error = null;
      state.fetchOptions = { ...state.fetchOptions, ...action.payload };
    },
    fetchMarketingTemplatesSuccess: (
      state,
      action: PayloadAction<{ data: MarketingTemplate[]; pagination: any }>,
    ) => {
      state.loading = false;
      state.items = action.payload.data;
      state.pagination = action.payload.pagination;
    },
    fetchMarketingTemplatesFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.loading = false;
      state.error = action.payload;
    },

    searchMarketingTemplatesRequest: (
      state,
      _action: PayloadAction<{
        query: string;
        options?: FetchMarketingTemplatesOptions;
      }>,
    ) => {
      state.searchLoading = true;
      state.searchError = null;
    },
    searchMarketingTemplatesSuccess: (
      state,
      action: PayloadAction<{ data: MarketingTemplate[]; pagination: any }>,
    ) => {
      state.searchLoading = false;
      state.searchResults = action.payload.data;
      state.searchPagination = action.payload.pagination;
    },
    searchMarketingTemplatesFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.searchLoading = false;
      state.searchError = action.payload;
    },

    fetchMarketingTemplateDetailsRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.templateLoading = true;
      state.templateError = null;
    },
    fetchMarketingTemplateDetailsSuccess: (
      state,
      action: PayloadAction<MarketingTemplate>,
    ) => {
      state.templateLoading = false;
      state.currentTemplate = action.payload;
    },
    fetchMarketingTemplateDetailsFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.templateLoading = false;
      state.templateError = action.payload;
    },

    createMarketingTemplateRequest: (
      state,
      _action: PayloadAction<CreateMarketingTemplateDto>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    createMarketingTemplateSuccess: (
      state,
      action: PayloadAction<MarketingTemplate>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items = [action.payload, ...state.items];
    },
    createMarketingTemplateFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    updateMarketingTemplateRequest: (
      state,
      _action: PayloadAction<{ id: string; data: UpdateMarketingTemplateDto }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    updateMarketingTemplateSuccess: (
      state,
      action: PayloadAction<MarketingTemplate>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.currentTemplate = action.payload;

      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    updateMarketingTemplateFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    deleteMarketingTemplateRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
    },
    deleteMarketingTemplateSuccess: (
      state,
      action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload.id);
    },
    deleteMarketingTemplateFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    setFilters: (state, action: PayloadAction<MarketingTemplateFilters>) => {
      state.filters = action.payload;
    },
    resetFormState: (state) => {
      state.formState = {
        loading: false,
        error: null,
        success: false,
      };
    },
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null;
      state.templateError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchMarketingTemplatesRequest,
  fetchMarketingTemplatesSuccess,
  fetchMarketingTemplatesFailure,
  searchMarketingTemplatesRequest,
  searchMarketingTemplatesSuccess,
  searchMarketingTemplatesFailure,
  fetchMarketingTemplateDetailsRequest,
  fetchMarketingTemplateDetailsSuccess,
  fetchMarketingTemplateDetailsFailure,
  createMarketingTemplateRequest,
  createMarketingTemplateSuccess,
  createMarketingTemplateFailure,
  updateMarketingTemplateRequest,
  updateMarketingTemplateSuccess,
  updateMarketingTemplateFailure,
  deleteMarketingTemplateRequest,
  deleteMarketingTemplateSuccess,
  deleteMarketingTemplateFailure,
  setFilters,
  resetFormState,
  clearCurrentTemplate,
} = marketingTemplateSlice.actions;

export default marketingTemplateSlice.reducer;
